// ============================================================
// MATCH-3 ENGINE (with animation support)
// ============================================================

import {
  M3_COLS, M3_ROWS, M3,
  basePointsForGroup, chainStepMult, chainPointsForGroup, sizeMultiplier,
  MATCH3_SIZE_COMBO_CAP,
} from './constants.ts';
import { ri } from './utils.ts';
import { anim } from './Anim.ts';
import { Gem } from './Gem.ts';
import { SeededRng } from './server/rng.ts';

// ============================================================
// Types — split between "per-step" (no scoring yet) and
// "chain-complete" (full scoring breakdown).
// ============================================================

/**
 * Raw connected component info (no scoring). Output of
 * `findMatchComponents` — for callers that want to decide the combo
 * depth based on the component count (e.g. the simul-bonus rule)
 * BEFORE applying multipliers.
 */
export interface MatchComponent {
  el: number;
  count: number;
  centerR: number;
  centerC: number;
}

/**
 * Group with full scoring fields filled in (after the chain mult is
 * known). Used in the per-step breakdown inside `ChainCompleteEvent`,
 * and as the return type of the back-compat `scoreComponents` /
 * `groupMatchedCells` helpers.
 */
export interface MatchGroup {
  el: number;
  count: number;
  /** Base points (no combo) = chainPointsForGroup(count, 1). */
  basePoints: number;
  /** Size-only multiplier (3 = 1.0, 4 = 2.0, 5 = 3.5, 6+ = 5.0). */
  sizeMult: number;
  /** Combo multiplier applied at this step (from `chainStepMult`). */
  comboMult: number;
  /** min(SIZE_COMBO_CAP, sizeMult × comboMult). */
  finalMult: number;
  /** Final points awarded = round(10 × finalMult). */
  points: number;
  centerR: number;
  centerC: number;
}

/**
 * Event emitted by `_onMatch` once per cascade step DURING a chain.
 * Carries no scoring info — the per-step combo multiplier depends on
 * the chain's eventual total depth (which isn't known until the chain
 * settles). Used by the Renderer for step-level visuals: group rings,
 * size labels (NICE!/BIG!), the small "C2/C3/..." step indicator,
 * and the simul-bonus tip.
 */
export interface ChainStepEvent {
  /** 1-indexed raw cascade step (1 = player swap match, 2+ = cascades). */
  comboDepth: number;
  /** Connected components found in this step (no scoring yet). */
  components: MatchComponent[];
  /** True iff this step has ≥ 2 components (simul-bonus rule, method B). */
  isSimulStep: boolean;
}

/**
 * Per-step scoring breakdown inside `ChainCompleteEvent`. One entry
 * per cascade step, with the final combo multiplier and per-group
 * point values now resolved.
 */
export interface ChainStepBreakdown {
  comboDepth: number;
  /** Multiplier this step received (from `chainStepMult`). */
  comboMult: number;
  /** Each group's scoring details. */
  groups: MatchGroup[];
}

/**
 * Event emitted by `_onChainComplete` exactly ONCE per swap, fired
 * when the chain stops producing new matches. Carries the full
 * scoring breakdown — Renderer uses this to drive the "+TotalPoints"
 * fly-to-HUD animation, the chain summary banner, and the actual
 * commit to `pRes` / `aRes`.
 */
export interface ChainCompleteEvent {
  /** Number of cascade steps that produced matches (≥ 1). */
  rawCascadeCount: number;
  /** Count of steps that contributed a simul-bonus (≥ 2 components). */
  simulBumps: number;
  /** Effective chain depth used for scoring = rawCascadeCount + simulBumps. */
  effChainDepth: number;
  /** Per-element total points awarded by this chain. */
  pointsByElement: Record<string, number>;
  /** Per-step breakdown for display / forensics. */
  perStep: ChainStepBreakdown[];
}

// ============================================================
// Component discovery (pure helpers, no state)
// ============================================================

/**
 * Partition a matched-cell set into connected components by 4-neighbour
 * adjacency + same element. No scoring.
 *
 * L / T / + shaped same-color matches collapse into a single component;
 * two non-touching 3-runs (even same color) count as two components.
 */
export function findMatchComponents(
  grid: ReadonlyArray<ReadonlyArray<Gem | null>>,
  matched: Set<number>,
): MatchComponent[] {
  if (matched.size === 0) return [];
  const visited = new Set<number>();
  const components: MatchComponent[] = [];
  for (const startIdx of matched) {
    if (visited.has(startIdx)) continue;
    const startR = Math.floor(startIdx / M3_COLS);
    const startC = startIdx % M3_COLS;
    const startGem = grid[startR][startC];
    if (!startGem) { visited.add(startIdx); continue; }
    const el = startGem.el;

    let sumR = 0, sumC = 0, count = 0;
    const queue: number[] = [startIdx];
    visited.add(startIdx);
    while (queue.length > 0) {
      const idx = queue.shift()!;
      const r = Math.floor(idx / M3_COLS);
      const c = idx % M3_COLS;
      sumR += r; sumC += c; count++;
      const neighbours: Array<[number, number]> = [
        [r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1],
      ];
      for (const [nr, nc] of neighbours) {
        if (nr < 0 || nr >= M3_ROWS || nc < 0 || nc >= M3_COLS) continue;
        const nIdx = nr * M3_COLS + nc;
        if (!matched.has(nIdx) || visited.has(nIdx)) continue;
        const ng = grid[nr][nc];
        if (!ng || ng.el !== el) continue;
        visited.add(nIdx);
        queue.push(nIdx);
      }
    }
    components.push({ el, count, centerR: sumR / count, centerC: sumC / count });
  }
  return components;
}

/**
 * Score raw components at a given combo multiplier. Pure function —
 * used by the AI scorer (Game._aiMatch3) and by the back-compat
 * `groupMatchedCells` wrapper.
 */
export function scoreComponents(
  components: ReadonlyArray<MatchComponent>,
  comboMult: number,
): MatchGroup[] {
  return components.map((c) => {
    const sm = sizeMultiplier(c.count);
    const finalMult = Math.min(MATCH3_SIZE_COMBO_CAP, sm * comboMult);
    return {
      el: c.el,
      count: c.count,
      basePoints: basePointsForGroup(c.count),
      sizeMult: sm,
      comboMult,
      finalMult,
      points: chainPointsForGroup(c.count, comboMult),
      centerR: c.centerR,
      centerC: c.centerC,
    };
  });
}

/**
 * Back-compat one-shot: find components + score them at a given
 * effective chain depth. NEW production code uses the chain pipeline
 * (M3Board._processChain → _finalizeChain) instead. This wrapper is
 * kept for tests and for the AI fallback path.
 *
 * `effChainDepth` is the EFFECTIVE chain depth (after simul-bonus
 * bumps) — the C1 step in that chain would receive `chainStepMult(1, N)`.
 */
export function groupMatchedCells(
  grid: ReadonlyArray<ReadonlyArray<Gem | null>>,
  matched: Set<number>,
  effChainDepth: number,
): MatchGroup[] {
  const comboMult = chainStepMult(1, effChainDepth);
  return scoreComponents(findMatchComponents(grid, matched), comboMult);
}

// ============================================================
// Board (stateful, animated)
// ============================================================

export class M3Board {
  grid: (Gem | null)[][] = [];
  locked = false;
  /**
   * Fired DURING a cascade, once per step. Carries no scoring (per-step
   * mult depends on the chain's final depth, not known mid-cascade).
   * Use for step-level visuals: rings, NICE!/BIG! labels, step badges,
   * simul tips.
   */
  _onMatch?: (event: ChainStepEvent) => void;
  /**
   * Fired ONCE per swap when the cascade chain settles (no more matches).
   * Carries the full scoring breakdown — drives the final +TotalPoints
   * fly-to-HUD animation, chain summary banner, and the actual write to
   * `pRes` / `aRes`.
   *
   * NOT fired when a swap produces zero matches (chain length 0).
   */
  _onChainComplete?: (event: ChainCompleteEvent) => void;
  /**
   * Deterministic RNG for gem generation. When null, falls back to
   * Math.random — that path is kept so pre-Phase-3c code paths / tests
   * that construct an M3Board without a seed still work.
   */
  rng: SeededRng | null;

  /**
   * Session 9 — explicit gem element pool (the active leader's
   * `gemElements`). When null, board rolls from the contiguous range
   * `M3.minGemElement..M3.maxGemElement` (back-compat). Set at battle
   * start by `Game.startBattle` so each player's board honours their
   * leader's palette (e.g. BBB rolls Fire / Water / Lightning / WindFriendly
   * / WindHostile — not the default 1..5).
   */
  elements: ReadonlyArray<number> | null;

  /**
   * Raw cascade step counter. Reset to 0 in `swap()`; incremented to 1
   * on the first `_processChain` call, then 2, 3, … per cascade.
   */
  comboDepth = 0;
  /**
   * Per-step buffer collected during the cascade. At chain end, fed
   * into the retroactive scoring formula in `_finalizeChain`.
   */
  private _chainSteps: { comboDepth: number; components: MatchComponent[] }[] = [];
  /** Count of cascade steps with ≥ 2 components (simul-bonus, method B). */
  private _chainSimulBumps = 0;

  constructor(rng: SeededRng | null = null, elements: ReadonlyArray<number> | null = null) {
    this.rng = rng;
    this.elements = elements;
    this.init();
  }

  private _rollElement(): number {
    if (this.elements && this.elements.length > 0) {
      const idx = this.rng
        ? this.rng.nextInt(0, this.elements.length - 1)
        : ri(0, this.elements.length - 1);
      return this.elements[idx];
    }
    return this.rng
      ? this.rng.nextInt(M3.minGemElement, M3.maxGemElement)
      : ri(M3.minGemElement, M3.maxGemElement);
  }

  init(): void {
    this.grid = [];
    for (let r = 0; r < M3_ROWS; r++) {
      this.grid[r] = [];
      for (let c = 0; c < M3_COLS; c++) {
        let el: number;
        // Re-roll until the new gem doesn't form an immediate 3-in-a-row.
        // NOTE: This means init consumes a VARIABLE number of RNG draws.
        // Callers sharing a seed across client+server must simulate this
        // same loop for state to match.
        do { el = this._rollElement(); } while (this._wouldMatch(r, c, el));
        this.grid[r][c] = new Gem(r, c, el);
      }
    }
  }

  private _wouldMatch(r: number, c: number, el: number): boolean {
    if (c >= 2 && this.grid[r][c - 1] && this.grid[r][c - 2] &&
      this.grid[r][c - 1]!.el === el && this.grid[r][c - 2]!.el === el) return true;
    if (r >= 2 && this.grid[r - 1] && this.grid[r - 2] &&
      this.grid[r - 1][c] && this.grid[r - 2][c] &&
      this.grid[r - 1][c]!.el === el && this.grid[r - 2][c]!.el === el) return true;
    return false;
  }

  swap(r1: number, c1: number, r2: number, c2: number): boolean | null {
    if (this.locked) return null;
    if (Math.abs(r1 - r2) + Math.abs(c1 - c2) !== 1) return null;
    if (r1 < 0 || r1 >= M3_ROWS || c1 < 0 || c1 >= M3_COLS) return null;
    if (r2 < 0 || r2 >= M3_ROWS || c2 < 0 || c2 >= M3_COLS) return null;

    const a = this.grid[r1][c1]!;
    const b = this.grid[r2][c2]!;

    // All swaps are valid (tactical swap) — no bounce-back
    this.grid[r1][c1] = b;
    this.grid[r2][c2] = a;
    a.r = r2; a.c = c2;
    b.r = r1; b.c = c1;

    this.locked = true;
    // Points & Tiers — every new swap starts a fresh combo chain. The
    // cascade buffer accumulates per-step records; `_finalizeChain`
    // resolves the retroactive mults when no more matches are found.
    this.comboDepth = 0;
    this._chainSteps = [];
    this._chainSimulBumps = 0;
    anim.add(a as any, { vx: c2, vy: r2 }, 0.12, 'easeOut');
    anim.add(b as any, { vx: c1, vy: r1 }, 0.12, 'easeOut', () => {
      this._processChain();
    });
    return true;
  }

  _processChain(): void {
    const matched = this._findMatches();
    if (matched.size === 0) {
      // Chain settles — finalize scoring (if any steps occurred) and unlock.
      if (this._chainSteps.length > 0) {
        this._finalizeChain();
      }
      this.locked = false;
      return;
    }

    // Raw cascade step.
    this.comboDepth++;
    const components = findMatchComponents(this.grid, matched);
    const isSimulStep = components.length >= 2;
    if (isSimulStep) this._chainSimulBumps++;
    this._chainSteps.push({ comboDepth: this.comboDepth, components });

    // Emit per-step event (no scoring — mult is unknown until chain ends).
    if (this._onMatch) this._onMatch({
      comboDepth: this.comboDepth,
      components,
      isSimulStep,
    });

    // Collect gems to remove, then animate
    const gemsToRemove: Gem[] = [];
    for (const idx of matched) {
      const r = Math.floor(idx / M3_COLS);
      const c = idx % M3_COLS;
      const gem = this.grid[r][c];
      if (gem) gemsToRemove.push(gem);
    }

    // Null out grid cells immediately (data) — animations are visual only
    for (const idx of matched) {
      const r = Math.floor(idx / M3_COLS);
      const c = idx % M3_COLS;
      this.grid[r][c] = null;
    }

    // Animate gem disappearance — single callback on last gem
    let remaining = gemsToRemove.length;
    for (const gem of gemsToRemove) {
      anim.add(gem as any, { scale: 0, alpha: 0 }, 0.18, 'easeOut', () => {
        remaining--;
        if (remaining <= 0) this._applyGravityAnimated();
      });
    }
  }

  /**
   * Resolve the buffered cascade into a single ChainCompleteEvent.
   * Called when `_processChain` finds no more matches but at least one
   * cascade step DID occur.
   *
   * Scoring formula (§2.4a, retroactive):
   *   effChainDepth = rawCascadeCount + simulBumps
   *   for each step k (1-indexed):
   *     comboMult = chainStepMult(k, effChainDepth)
   *     for each group: points = chainPointsForGroup(group.count, comboMult)
   */
  private _finalizeChain(): void {
    const rawCascadeCount = this._chainSteps.length;
    const simulBumps = this._chainSimulBumps;
    const effChainDepth = rawCascadeCount + simulBumps;

    const pointsByElement: Record<string, number> = {};
    const perStep: ChainStepBreakdown[] = [];

    for (const step of this._chainSteps) {
      const comboMult = chainStepMult(step.comboDepth, effChainDepth);
      const groups = scoreComponents(step.components, comboMult);
      for (const g of groups) {
        const key = String(g.el);
        pointsByElement[key] = (pointsByElement[key] || 0) + g.points;
      }
      perStep.push({ comboDepth: step.comboDepth, comboMult, groups });
    }

    if (this._onChainComplete) this._onChainComplete({
      rawCascadeCount,
      simulBumps,
      effChainDepth,
      pointsByElement,
      perStep,
    });

    // Reset buffer for next chain.
    this._chainSteps = [];
    this._chainSimulBumps = 0;
  }

  private _applyGravityAnimated(): void {
    let totalAnims = 0;
    let finishedAnims = 0;

    const onAnimDone = (): void => {
      finishedAnims++;
      if (finishedAnims >= totalAnims) {
        // All gravity animations done — check for new chains
        this._processChain();
      }
    };

    for (let c = 0; c < M3_COLS; c++) {
      let writeRow = M3_ROWS - 1;
      for (let r = M3_ROWS - 1; r >= 0; r--) {
        if (this.grid[r][c]) {
          if (r !== writeRow) {
            const gem = this.grid[r][c]!;
            this.grid[writeRow][c] = gem;
            this.grid[r][c] = null;
            const dropDist = writeRow - gem.r;
            gem.r = writeRow; gem.c = c;
            totalAnims++;
            anim.add(gem as any, { vy: writeRow }, 0.07 * dropDist + 0.08, 'bounce', onAnimDone);
          }
          writeRow--;
        }
      }
      let fillCount = 0;
      for (let r = writeRow; r >= 0; r--) {
        // New gem's element is drawn from this board's RNG for determinism.
        // Pass `this.elements` so leader-specific gem pools (Session 9)
        // also apply to cascade-fill gems, not just the initial board.
        const gem = new Gem(r, c, undefined, this.rng, this.elements ?? undefined);
        gem.vy = -1 - fillCount;
        this.grid[r][c] = gem;
        fillCount++;
        totalAnims++;
        anim.add(gem as any, { vy: r }, 0.07 * (fillCount + r) + 0.1, 'bounce', onAnimDone);
      }
    }

    // Edge case: nothing moved (shouldn't happen, but just in case)
    if (totalAnims === 0) this._processChain();
  }

  _findMatches(): Set<number> {
    const matched = new Set<number>();
    for (let r = 0; r < M3_ROWS; r++) {
      for (let c = 0; c <= M3_COLS - 3; c++) {
        if (!this.grid[r][c]) continue;
        const t = this.grid[r][c]!.el;
        let len = 1;
        while (c + len < M3_COLS && this.grid[r][c + len] && this.grid[r][c + len]!.el === t) len++;
        if (len >= 3) for (let i = 0; i < len; i++) matched.add(r * M3_COLS + c + i);
      }
    }
    for (let c = 0; c < M3_COLS; c++) {
      for (let r = 0; r <= M3_ROWS - 3; r++) {
        if (!this.grid[r][c]) continue;
        const t = this.grid[r][c]!.el;
        let len = 1;
        while (r + len < M3_ROWS && this.grid[r + len][c] && this.grid[r + len][c]!.el === t) len++;
        if (len >= 3) for (let i = 0; i < len; i++) matched.add((r + i) * M3_COLS + c);
      }
    }
    return matched;
  }

  getAllGems(): Gem[] {
    const gems: Gem[] = [];
    for (let r = 0; r < M3_ROWS; r++)
      for (let c = 0; c < M3_COLS; c++)
        if (this.grid[r][c]) gems.push(this.grid[r][c]!);
    return gems;
  }

  /**
   * Story Mode — replace the entire grid with a fixed preset (e.g.
   * a pre-designed tutorial board). Bypasses the no-initial-match
   * check in `init()` so the caller has full control.
   *
   * Caller is expected to provide a 6×6 array of element ids
   * (1..M3.maxGemElement). Cells with element 0 / out of range fall
   * back to a random roll.
   *
   * Resets cascade state and unlocks the board so the next `swap()`
   * is accepted immediately.
   */
  loadFromPreset(grid: number[][]): void {
    anim.clear();
    this.grid = [];
    for (let r = 0; r < M3_ROWS; r++) {
      this.grid[r] = [];
      for (let c = 0; c < M3_COLS; c++) {
        const el = grid[r]?.[c];
        const useEl = (typeof el === 'number' && el >= M3.minGemElement && el <= M3.maxGemElement)
          ? el
          : this._rollElement();
        this.grid[r][c] = new Gem(r, c, useEl);
      }
    }
    this.locked = false;
    this.comboDepth = 0;
    this._chainSteps = [];
    this._chainSimulBumps = 0;
  }
}
